import { useEffect, useRef, useState } from 'react'

// Lazy-load mermaid so the 1+MB library only ships when a customer opens a
// doc that contains a diagram (most don't).
type MermaidLib = { initialize: (cfg: Record<string, unknown>) => void; render: (id: string, code: string) => Promise<{ svg: string }> }
let mermaidPromise: Promise<MermaidLib> | null = null
function getMermaid(): Promise<MermaidLib> {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(m => {
      const lib = (m as { default: MermaidLib }).default
      lib.initialize({
        startOnLoad: false,
        theme: 'dark',
        securityLevel: 'strict',
        fontFamily: 'system-ui, sans-serif',
      })
      return lib
    })
  }
  return mermaidPromise
}

export function MermaidDiagram({ code }: { code: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    getMermaid().then(mermaid => {
      const id = `m-${Math.random().toString(36).slice(2)}`
      return mermaid.render(id, code)
    }).then(({ svg }) => {
      if (cancelled || !ref.current) return
      ref.current.innerHTML = svg
    }).catch((e: unknown) => {
      if (cancelled) return
      const msg = e instanceof Error ? e.message : String(e)
      setError(msg)
    })
    return () => { cancelled = true }
  }, [code])

  if (error) {
    // The "text/html is not a valid JavaScript MIME type" pattern means a
    // dynamic-import chunk URL fell through to the SPA HTML fallback — almost
    // always because the tab is running a stale admin build after a redeploy.
    // The window-level handler in main.tsx will trigger a one-shot reload; we
    // also render a manual button in case the auto-reload was suppressed.
    const isStaleChunk =
      error.includes('is not a valid JavaScript MIME type') ||
      error.includes('Failed to fetch dynamically imported module') ||
      error.includes('Importing a module script failed')
    return (
      <div className="my-4 p-4 rounded bg-[#2a1717] border border-red-900/40 text-red-200 text-sm">
        <p className="font-semibold mb-1">Diagram failed to render</p>
        <p className="text-xs text-red-300/80 mb-3">
          {isStaleChunk
            ? "Your browser cached an older version of the admin. Reload to pull the latest build."
            : `Mermaid: ${error}`}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-3 py-1.5 text-xs rounded bg-[#C41E3A] hover:bg-[#a3172e] text-white font-medium"
        >
          Reload page
        </button>
      </div>
    )
  }
  return (
    <div
      ref={ref}
      className="my-6 flex justify-center overflow-x-auto [&_svg]:max-w-full [&_svg]:h-auto"
    />
  )
}
